import {
  ACTION,
  EnterDraftEdit,
  LeaveDraftEdit,
  RequestDraftDocument,
  ReceiveDraftDocument,
  FailDraftDocument,
  AddDocumentItem,
  ChildItemSelection,
  DeleteDocumentItem,
  EnterConnectedDraftEdit,
  CopyDocumentItem,
  ShowConfirmWarnings,
  WarningsConfirmed,
  TriggerValidation,
  ReceiveUiDefinition,
  ClearUiDefinition
} from './action-types';
import { CaseState } from 'app/area/case/types-store';
import { SchemaBuilder } from 'app/infrastructure/ui/builder';
import { RenderMode } from 'app/types-shared';
import { ActionMap } from 'app/area/case';
import {
  insertChild,
  createChild,
  deleteChild,
  copyChild
} from 'app/infrastructure/store';
import { reducers as changeCaseTypeReducers } from './change-casetype/reducers';

export const reducers: ActionMap = {
  ...changeCaseTypeReducers,

  [ACTION.ENTER_DRAFT_EDIT]: (
    state: CaseState,
    action: EnterDraftEdit
  ): CaseState => {
    return {
      ...state,
      ...{
        renderMode: RenderMode.edit,
        schema: SchemaBuilder.create(RenderMode.edit, action.uiDefinition),
        uiDefinition: action.uiDefinition,
        activeChildItem: undefined
      }
    };
  },

  [ACTION.REQUEST_DRAFT_DOC]: (
    state: CaseState,
    action: RequestDraftDocument
  ): CaseState => {
    return {
      ...state,
      ...{
        fetching: true
      }
    };
  },

  [ACTION.RECEIVE_DRAFT_DOC]: (
    state: CaseState,
    action: ReceiveDraftDocument
  ): CaseState => {
    return {
      ...state,
      ...{
        fetching: false,
        editing: {
          ...state.editing,

          saving: false,
          etag: action.etag,
          hasChanges: false,
          persistedValues: action.data,
          values: action.data,
          metadata: action.metadata
        }
      }
    };
  },

  [ACTION.FAIL_DRAFT_DOC]: (
    state: CaseState,
    action: FailDraftDocument
  ): CaseState => {
    return {
      ...state,
      ...{
        fetching: false,
        editing: undefined
      }
    };
  },

  [ACTION.LEAVE_DRAFT_EDIT]: (
    state: CaseState,
    action: LeaveDraftEdit
  ): CaseState => {
    return {
      ...state,
      editing: undefined,
      renderMode: RenderMode.read,
      schema: undefined,
      activeChildItem: undefined
    };
  },

  [ACTION.COPY_DOCUMENT_ITEM]: (
    state: CaseState,
    action: CopyDocumentItem
  ): CaseState => {
    let nextValues = state.editing.values;
    for(let i = 0; i < action.options.noOfCopies; i++){
      nextValues = copyChild(nextValues,action.documentItemPath);
    }
    return {
      ...state,
      ...{
        editing: {
          ...state.editing,

          hasChanges: true,

          values: nextValues
        }
      }
    };
  },

  [ACTION.ADD_DOCUMENT_ITEM]: (
    state: CaseState,
    action: AddDocumentItem
  ): CaseState => {
    const newChild = createChild(
      action.parentKeys,
      action.entityType,
      action.childId,
      action.initialValues
    );

    const newCaseValues = insertChild(state.editing.values, newChild);

    // Unchanged state if the child was not inserted. This happens when there is already a child by the same id.
    if (!newCaseValues) {
      return { ...state };
    }

    return {
      ...state,
      ...{
        editing: {
          ...state.editing,

          hasChanges: true,

          values: newCaseValues
        }
      }
    };
  },

  [ACTION.DELETE_DOCUMENT_ITEM]: (
    state: CaseState,
    action: DeleteDocumentItem
  ): CaseState => {
    const newCaseValues = deleteChild(state.editing.values, action.childKey);

    return {
      ...state,
      ...{
        editing: {
          ...state.editing,

          hasChanges: true,

          values: newCaseValues
        }
      }
    };
  },

  [ACTION.SELECT_DOCUMENT_ITEM]: (
    state: CaseState,
    action: ChildItemSelection
  ): CaseState => {
    return {
      ...state,
      ...{
        activeChildItem: action.key
      }
    };
  },

  [ACTION.ENTER_CONNECTED_DRAFT_EDIT]: (
    state: CaseState,
    action: EnterConnectedDraftEdit
  ): CaseState => {
    return {
      ...state,
      ...{
        editing: {
          ...state.editing,
          connectedDrafts: action.connections
        }
      }
    };
  },

  [ACTION.SHOW_CONFIRM_WARNINGS]: (
    state: CaseState,
    action: ShowConfirmWarnings
  ): CaseState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        showConfirmWarnings: action.value,
        warnings: action.warnings
      }
    };
  },

  [ACTION.WARNINGS_CONFIRMED]: (
    state: CaseState,
    action: WarningsConfirmed
  ): CaseState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        warningsConfirmed: action.value
      }
    };
  },

  [ACTION.TRIGGER_VALIDATION]: (
    state: CaseState,
    action: TriggerValidation
  ): CaseState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        loadValidationMetadata: true
      }
    };
  },

  [ACTION.RECEIVE_UI_DEFINITION]: (
    state: CaseState,
    action: ReceiveUiDefinition
  ): CaseState => {
    return {
      ...state,
      schema: SchemaBuilder.create(RenderMode.edit, action.definition),
      uiDefinition: action.definition
    };
  },

  [ACTION.CLEAR_UI_DEFINITION]: (
    state: CaseState,
    action: ClearUiDefinition
  ): CaseState => {
    return {
      ...state,
      schema: undefined,
      uiDefinition: undefined
    };
  }
};
